import './ConfigbaseNav.css'

import { gql } from '@apollo/client'
import { CaretDown } from 'arp/design-system/icons/caret-down'
import { CaretRight } from 'arp/design-system/icons/caret-right'
import { clsx } from 'clsx'
import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Link, matchPath, useLocation } from 'react-router'
import {
  type NavigationItemsQuery,
  useNavigationItemsQuery,
} from '~/common/generated/apollo-operations'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossIcon } from '~/components/DossIcon'

gql`
  query NavigationItems {
    navigationItems {
      id
      parentId
      order
      label
      icon
      href
      destination {
        __typename
      }
    }
  }
`

export type NavigationItem = NavigationItemsQuery['navigationItems'][number]

type NavigationNode = NavigationItem & { children: NavigationNode[] }

/**
 * The server's flat item list as a tree, siblings sorted by `order`.
 * A header (no `href`) whose children were all pruned by permissions has nothing to show, so it's
 * dropped with them.
 */
export const buildNavigationTree = (items: readonly NavigationItem[]): NavigationNode[] => {
  const childrenOf = items.reduce(
    (map, item) => map.set(item.parentId, [...(map.get(item.parentId) ?? []), item]),
    new Map<string | null, NavigationItem[]>(),
  )
  const build = (parentId: string | null): NavigationNode[] =>
    (childrenOf.get(parentId) ?? [])
      .toSorted((a, b) => a.order - b.order)
      .flatMap((item) => {
        const children = build(item.id)
        return item.href === null && children.length === 0 ? [] : [{ ...item, children }]
      })
  return build(null)
}

/**
 * The one item the current location belongs to.
 * An item matches when its pathname prefixes the location's and every query param it carries has
 * the same value there. The most specific match wins (longest pathname, then most params), so
 * "Open" and "Late" on one route, or a table and its tabs, light separately, while a guest's
 * deeper route with no item of its own keeps its app's item lit.
 */
export const activeItemId = (
  items: readonly NavigationItem[],
  location: { pathname: string; search: string },
): string | null => {
  const current = new URLSearchParams(location.search)
  const matches = items.flatMap((item) => {
    if (item.href === null) {
      return []
    }
    const url = new URL(item.href, 'https://navigation.invalid')
    const params = [...url.searchParams]
    const matched =
      matchPath({ path: url.pathname, end: false }, location.pathname) !== null &&
      params.every(([key, value]) => current.get(key) === value)
    return matched ? [{ id: item.id, pathLength: url.pathname.length, params: params.length }] : []
  })
  const best = matches.toSorted((a, b) => b.pathLength - a.pathLength || b.params - a.params).at(0)
  return best?.id ?? null
}

// The first two levels of the tree are the app and page tiers; everything deeper is a sub-page.
type NavTier = 'app' | 'page' | 'subpage'

const tierOf = (depth: number): NavTier => {
  if (depth === 0) {
    return 'app'
  }
  return depth === 1 ? 'page' : 'subpage'
}

/** The icon a node shows when nav.ts gives it none, by what it links to. */
const FALLBACK_ICONS: Record<NonNullable<NavigationItem['destination']>['__typename'], string> = {
  Table: 'table',
  TableTab: 'table',
  Embed: 'app-window',
  Workspace: 'layout',
  FormV2: 'note-pencil',
}

// nav.ts icon tokens are DossIcon asset names, e.g. `shopping-cart`.
const iconNameOf = (node: NavigationNode): string =>
  `${node.icon ?? (node.destination ? FALLBACK_ICONS[node.destination.__typename] : 'folder')}.svg`

interface NavRowProps {
  node: NavigationNode
  depth: number
  isActive: boolean
  /** Branches only: whether the children show. Leaves pass neither this nor `onToggle`. */
  isExpanded?: boolean | undefined
  onToggle?: (() => void) | undefined
}

/**
 * One row: a full-height link (the whole row is the click target; selection is its
 * `aria-current`), preceded on branches by a disclosure toggle whose icon swaps to a caret on
 * hover. A header has no link, so its label sits in a plain span.
 */
const NavRow = ({ node, depth, isActive, isExpanded, onToggle }: NavRowProps): ReactElement => {
  const { t } = useTypedTranslation()
  const { dispatch } = useDossSignals()
  const tier = tierOf(depth)
  const icon =
    tier === 'subpage' ? null : <DossIcon className="icon" name={iconNameOf(node)} size={20} />
  const Caret = isExpanded === true ? CaretDown : CaretRight
  const content = (
    <>
      {onToggle === undefined && icon !== null && <span className="leading">{icon}</span>}
      <span className="label">{node.label}</span>
    </>
  )

  return (
    <li className={clsx('row', tier)}>
      {onToggle !== undefined && (
        <button
          aria-expanded={isExpanded}
          aria-label={
            isExpanded === true
              ? t('left_nav.collapse_item', { name: node.label })
              : t('left_nav.expand_item', { name: node.label })
          }
          className="disclosure"
          onClick={onToggle}
          type="button"
        >
          {icon}
          <Caret className="caret" />
        </button>
      )}
      {node.href === null ? (
        <span className="link">{content}</span>
      ) : (
        <Link
          aria-current={isActive ? 'page' : undefined}
          className="link"
          onClick={() => {
            // Auto-close mobile nav when selecting a new page; platform standard pattern.
            dispatch(DossSignalType.TOGGLE_MOBILE_MENU, false)
          }}
          to={node.href}
        >
          {content}
        </Link>
      )}
    </li>
  )
}

/**
 * The org's primary navigation as deployed from its configbase `nav.ts`: a nestable tree rendered
 * as one list of rows, depth shown by indent.
 */
export const ConfigbaseNav = () => {
  const { data } = useNavigationItemsQuery()
  const location = useLocation()
  const items = data?.navigationItems
  const tree = useMemo(() => (items ? buildNavigationTree(items) : []), [items])
  const activeId = items ? activeItemId(items, location) : null
  // Manual toggles, kept until the active item changes and reopens the path to it
  const [toggled, setToggled] = useState({ activeId, open: new Map<string, boolean>() })

  if (!items) {
    return null
  }
  const open = toggled.activeId === activeId ? toggled.open : new Map<string, boolean>()
  const parentOf = new Map(items.map((item) => [item.id, item.parentId]))
  const pathTo = (id: string | null | undefined): string[] =>
    id === null || id === undefined ? [] : [id, ...pathTo(parentOf.get(id))]
  const activePath = new Set(pathTo(activeId))

  const renderNode = (node: NavigationNode, depth: number): ReactElement[] => {
    const isBranch = node.children.length > 0
    // Headers and the current page's branch and its ancestors start open; the rest start collapsed.
    const isExpanded =
      isBranch && (open.get(node.id) ?? (node.href === null || activePath.has(node.id)))
    return [
      <NavRow
        key={node.id}
        depth={depth}
        isActive={node.id === activeId}
        isExpanded={isBranch ? isExpanded : undefined}
        node={node}
        onToggle={
          isBranch
            ? () => {
                setToggled({ activeId, open: new Map(open).set(node.id, !isExpanded) })
              }
            : undefined
        }
      />,
      ...(isExpanded ? node.children.flatMap((child) => renderNode(child, depth + 1)) : []),
    ]
  }

  return <ul className="arp-root configbase-nav">{tree.flatMap((node) => renderNode(node, 0))}</ul>
}
